import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Bell, Calendar, Clock } from 'lucide-react';

export const REMINDER_OFFSETS = [
  { value: 'none', label: 'No reminder' },
  { value: 'at_time', label: 'At time of event' },
  { value: '10min', label: '10 minutes before' },
  { value: '30min', label: '30 minutes before' },
  { value: '1hour', label: '1 hour before' },
  { value: '2hour', label: '2 hours before' },
];

export const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (e) {
    return 'UTC';
  }
};

/**
 * Reusable Date + Time + Reminder picker used by Tasks and Contact Follow-Ups.
 * value: { date, time, offset }  onChange receives the full next value.
 */
export const ReminderField = ({
  value = {},
  onChange,
  accent = 'emerald',
  dateLabel = 'Date',
  timeLabel = 'Time',
  testPrefix = 'reminder',
}) => {
  const date = value.date || '';
  const time = value.time || '';
  const offset = value.offset || 'none';

  const set = (patch) => onChange({ date, time, offset, ...patch });

  const ring = { pink: 'focus:border-pink-400', amber: 'focus:border-amber-400' }[accent] || 'focus:border-emerald-400';

  return (
    <div className="space-y-3 rounded-xl border p-3 bg-muted/30" data-testid={`${testPrefix}-field`}>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs flex items-center gap-1">
            <Calendar className="w-3 h-3" /> {dateLabel}
          </Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => {
              const d = e.target.value;
              // picking a date with no time yet defaults to 9:00 AM
              set({ date: d, time: d && !time ? '09:00' : time });
            }}
            className={`text-sm ${ring}`}
            data-testid={`${testPrefix}-date`}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs flex items-center gap-1">
            <Clock className="w-3 h-3" /> {timeLabel}
          </Label>
          <Input
            type="time"
            value={time}
            onChange={(e) => set({ time: e.target.value })}
            disabled={!date}
            className={`text-sm ${ring}`}
            data-testid={`${testPrefix}-time`}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs flex items-center gap-1">
          <Bell className="w-3 h-3" /> Remind me
        </Label>
        <Select value={offset} onValueChange={(v) => set({ offset: v })} disabled={!date}>
          <SelectTrigger data-testid={`${testPrefix}-offset`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {REMINDER_OFFSETS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!date && (
          <p className="text-[11px] text-muted-foreground">Pick a date to enable a reminder.</p>
        )}
      </div>
    </div>
  );
};

export default ReminderField;
